import { NavLink } from "react-router-dom";
import { useState } from "react";

/* ===============================
   NavItem Component
================================ */
const NavItem = ({
  to,
  icon,
  label,
}: {
  to: string;
  icon: string;
  label: string;
}) => (
  <NavLink
    to={to}
    end
    className={({ isActive }) =>
      `app-sidebar__nav-link ${isActive ? "app-sidebar__nav-link--active" : ""}`
    }
  >
    <i className={`fas ${icon} app-sidebar__nav-icon`} />
    <span className="app-sidebar__nav-label">{label}</span>
  </NavLink>
);

/* ===============================
   MenuGroup Component
================================ */
const MenuGroup = ({
  title,
  open,
  onClick,
  children,
}: {
  title: string;
  open: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <div className="app-sidebar__group">
    <button
      type="button"
      className="app-sidebar__group-title"
      onClick={onClick}
      aria-expanded={open}
    >
      <span>{title}</span>
      <i className={`fas fa-chevron-${open ? "down" : "right"} app-sidebar__group-chevron`} />
    </button>
    {open && <div className="app-sidebar__group-items">{children}</div>}
  </div>
);

/* ===============================
   Sidebar Component
================================ */
const Sidebar = ({
  logo,
  token,
  isAuthenticated,
  logout,
}: {
  logo: string;
  token?: any;
  isAuthenticated?: boolean;
  logout?: () => void;
}) => {
  const [open, setOpen] = useState<string | null>("dashboard");

  const toggle = (key: string) => setOpen(open === key ? null : key);

  return (
    <>
      <aside className="app-sidebar">
        <div className="app-sidebar__logo">
          <img src={logo} alt="Logo" className="app-sidebar__logo-img" />
        </div>

        <nav className="app-sidebar__nav">
          <NavItem to="/" icon="fa-home" label="Home" />

          <MenuGroup
            title="Attendance & Tracking"
            open={open === "attendance"}
            onClick={() => toggle("attendance")}
          >
            <NavItem to="/daily-attendance" icon="fa-calendar-check" label="Daily Attendances" />
            <NavItem to="/location-tracking" icon="fa-map-location-dot" label="Geofence Map Report" />
          </MenuGroup>

          <MenuGroup
            title="Masters"
            open={open === "masters"}
            onClick={() => toggle("masters")}
          >
            <NavItem to="/ports" icon="fa-ship" label="Locations" />
            <NavItem to="/terminals" icon="fa-location-dot" label="Geo Fence Location" />
            <NavItem to="/shifts" icon="fa-clock" label="Shifts" />
            <NavItem to="/employees" icon="fa-users" label="Employees" />
            <NavItem to="/designations" icon="fa-user-tie" label="Designations" />
            <NavItem to="/targets" icon="fa-bullseye" label="Engineers Targets" />
            <NavItem to="/available-count" icon="fas fa-users" label="Agreed Manpower" />
          </MenuGroup>

          <MenuGroup
            title="Assets & Sites"
            open={open === "assets"}
            onClick={() => toggle("assets")}
          >
            <NavItem to="/equipments" icon="fa-toolbox" label="Equipment" />
            {token && token.employee_code === "super" && (
              <NavItem to="/site-type" icon="fa-layer-group" label="Site Types" />
            )}
            <NavItem to="/sites" icon="fa-building" label="Site Master" />
            <NavItem to="/site-places" icon="fa-map-pin" label="RFID Master" />
          </MenuGroup>

          <MenuGroup
            title="Reports"
            open={open === "reports"}
            onClick={() => toggle("reports")}
          >
            <NavItem to="/preventive-maintenance" icon="fa-clipboard-list" label="PM Report" />
            <NavItem to="/engineer-task-dashboard" icon="fa-tasks" label="Engineers Task Dashboard" />
            <NavItem to="/manpower-dashboard" icon="fa-users-gear" label="Manpower Dashboard" />
          </MenuGroup>
        </nav>

        {isAuthenticated && logout && (
          <div className="app-sidebar__footer">
            <button
              type="button"
              className="app-sidebar__logout"
              onClick={logout}
            >
              <i className="fas fa-sign-out-alt" />
              <span>Logout</span>
            </button>
          </div>
        )}
      </aside>

      <style>{`
        .app-sidebar {
          position: fixed;
          left: 0;
          top: 0;
          width: 260px;
          height: 100vh;
          z-index: 1040;
          display: flex;
          flex-direction: column;
          background: linear-gradient(90deg, #156082 0%, #156082 100%);
          border-right: 1px solid rgba(255, 255, 255, 0.12);
          box-shadow: 4px 0 24px rgba(0, 0, 0, 0.15);
        }

        .app-sidebar__logo {
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 60px;
          padding: 0px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.15);
          background: rgba(255, 255, 255);
        }

        .app-sidebar__logo-img {
          max-height: 62px;
          width: auto;
          object-fit: contain;
        }

        .app-sidebar__nav {
          flex: 1;
          overflow-y: auto;
          overflow-x: hidden;
          padding: 12px 10px 16px;
        }

        .app-sidebar__nav::-webkit-scrollbar {
          width: 5px;
        }

        .app-sidebar__nav::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.1);
        }

        .app-sidebar__nav::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.25);
          border-radius: 10px;
        }

        .app-sidebar__nav-link {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 12px;
          margin-bottom: 2px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          color: rgba(255, 255, 255, 0.85);
          text-decoration: none;
          transition: background 0.2s ease, color 0.2s ease;
          position: relative;
          border: none;
          width: 100%;
          text-align: left;
          background: transparent;
          cursor: pointer;
        }

        .app-sidebar__nav-link:hover {
          background: rgba(255, 255, 255, 0.12);
          color: #fff;
        }

        .app-sidebar__nav-link--active {
          background: rgba(255, 255, 255, 0.18);
          color: #fff;
          font-weight: 600;
        }

        .app-sidebar__nav-link--active::before {
          content: "";
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
          width: 3px;
          height: 20px;
          background: #fff;
          border-radius: 0 3px 3px 0;
        }

        .app-sidebar__nav-icon {
          width: 18px;
          font-size: 15px;
          color: rgba(255, 255, 255, 0.9);
          flex-shrink: 0;
        }

        .app-sidebar__nav-link--active .app-sidebar__nav-icon {
          color: #fff;
        }

        .app-sidebar__nav-label {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .app-sidebar__group {
          margin-top: 16px;
        }

        .app-sidebar__group:first-of-type {
          margin-top: 8px;
        }

        .app-sidebar__group-title {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          padding: 8px 12px;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: rgba(255, 255, 255, 0.65);
          background: transparent;
          border: none;
          cursor: pointer;
          transition: color 0.2s ease;
          border-radius: 6px;
        }

        .app-sidebar__group-title:hover {
          color: #fff;
        }

        .app-sidebar__group-chevron {
          font-size: 10px;
          color: rgba(255, 255, 255, 0.7);
          transition: transform 0.2s ease;
        }

        .app-sidebar__group-items {
          padding-left: 6px;
          margin-top: 2px;
        }

        .app-sidebar__footer {
          flex-shrink: 0;
          padding: 12px 10px 16px;
          border-top: 1px solid rgba(255, 255, 255, 0.15);
          background: rgba(0, 0, 0, 0.08);
        }

        .app-sidebar__logout {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          width: 100%;
          padding: 10px 14px;
          font-size: 14px;
          font-weight: 500;
          color: rgba(0, 0, 0, 0.9);
          background: rgba(252, 255, 65, 0.9);
          border: 1px solid rgba(255, 255, 255, 0.35);
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .app-sidebar__logout:hover {
          background: rgba(255, 255, 255, 0.15);
          color: #fff;
          border-color: rgba(255, 255, 255, 0.5);
        }

        .app-sidebar__logout i {
          color: inherit;
        }
      `}</style>
    </>
  );
};

export default Sidebar;
